// src/hooks/useAuditStore.ts
// REFACTORED STORE - Single Source of Truth: V3 Master Engine

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useEffect, useState } from "react";
import type { MasterResult } from "@/lib/master-engine";
import { calculateAuditResult } from "@/lib/calculate-result";

interface CompanyProfile {
  companyName?: string;
  companyDomain?: string; // NEW: Website domain for AI enrichment
  industry?: string;
  businessModel?: string;
  teamSize?: string | number;
  acquisitionMethods?: Record<string, number>;
  [key: string]: unknown;
}

type AuditStore = {
  // Company Profile
  companyProfile: CompanyProfile | null;

  // Navigation
  currentStep: number;
  currentCategoryIndex: number;

  // Raw Answers (unverändert)
  rawAnswers: Record<string, unknown>;

  // Result (V3 Master Engine Output)
  result: MasterResult | null;

  // Legacy compatibility
  hasSavedSubmission: boolean;
  answers: Record<string, unknown>; // Alias for rawAnswers
  masterResult: MasterResult | null; // Alias for result
  dynamicSignals: unknown; // Legacy

  // Actions
  setHasSavedSubmission: (value: boolean) => void;
  setCompanyProfile: (value: CompanyProfile) => void;
  setCurrentStep: (value: number) => void;
  setCurrentCategoryIndex: (value: number) => void;
  saveAnswer: (questionId: string, value: unknown) => void;
  setRawAnswers: (value: Record<string, unknown>) => void;
  setAnswers: (value: Record<string, unknown>) => void; // Alias for setRawAnswers
  setDynamicSignals: (value: unknown) => void; // Legacy
  calculateResult: () => void;
  resetAudit: () => void;
  getProgress: () => number;
};

export const useAuditStore = create<AuditStore>()(
  persist(
    (set, get) => ({
      companyProfile: null as CompanyProfile | null,
      currentStep: 0,
      currentCategoryIndex: 0,
      rawAnswers: {} as Record<string, unknown>,
      result: null as MasterResult | null,
      hasSavedSubmission: false,
      dynamicSignals: null as unknown,

      // Legacy aliases (computed properties)
      get answers() {
        return get().rawAnswers;
      },
      get masterResult() {
        return get().result;
      },
      setHasSavedSubmission: (value) => set({ hasSavedSubmission: value }),

      setCompanyProfile: (value) => set({ companyProfile: value, currentStep: 1 }),

      setCurrentStep: (value) => set({ currentStep: value }),

      setCurrentCategoryIndex: (value) => set({ currentCategoryIndex: value }),

      saveAnswer: (questionId, value) =>
        set((state) => ({
          rawAnswers: {
            ...state.rawAnswers,
            [questionId]: value,
          },
        })),

      setRawAnswers: (value) => set({ rawAnswers: value }),

      setAnswers: (value) => set({ rawAnswers: value }), // Alias

      setDynamicSignals: (value) => set({ dynamicSignals: value }), // Legacy

      calculateResult: () => {
        const { rawAnswers, companyProfile } = get();

        if (!companyProfile) {
          console.error("❌ calculateResult: No company profile found");
          return;
        }

        try {
          const result = calculateAuditResult(rawAnswers, companyProfile);

          set({ result });
        } catch (error) {
          console.error("❌ calculateResult failed:", error);
        }
      },

      resetAudit: () =>
        set({
          companyProfile: null,
          currentStep: 0,
          currentCategoryIndex: 0,
          rawAnswers: {},
          result: null,
          hasSavedSubmission: false,
        }),

      getProgress: () => {
        const { rawAnswers } = get();
        // 61 = echte Diagnose-Fragen (MASTER_DIAGNOSTIC_QUESTIONS.length)
        // + ca. 20 Kontext-/Funnel-/Rep-Felder im Wizard
        const TOTAL_AUDIT_FIELDS = 81;
        const answeredQuestions = Object.keys(rawAnswers).filter(
          (k) => !k.endsWith('Unknown') && !k.endsWith('Exact') && !k.startsWith('_')
        ).length;
        return Math.min(100, Math.round((answeredQuestions / TOTAL_AUDIT_FIELDS) * 100));
      },
    }),
    {
      name: "salesforge-audit-storage",
      partialize: (state) => ({
        companyProfile: state.companyProfile as CompanyProfile | null,
        currentStep: state.currentStep,
        currentCategoryIndex: state.currentCategoryIndex,
        rawAnswers: state.rawAnswers as Record<string, unknown>,
        result: state.result as MasterResult | null,
        hasSavedSubmission: state.hasSavedSubmission,
      }),
    }
  )
);

/**
 * getResultFromStorage — liest MasterResult direkt und SYNCHRON aus dem localStorage.
 */
export function getResultFromStorage(): MasterResult | null {
  try {
    const raw = localStorage.getItem('salesforge-audit-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.state?.result as MasterResult) ?? null;
  } catch {
    return null;
  }
}

/**
 * getFullStateFromStorage — liest den kompletten Store-State synchron aus dem localStorage.
 * Gibt result UND companyProfile zurück.
 */
export function getFullStateFromStorage(): { result: MasterResult | null; companyProfile: unknown } {
  try {
    const raw = localStorage.getItem('salesforge-audit-storage');
    if (!raw) return { result: null, companyProfile: null };
    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? {};
    return {
      result: (state.result as MasterResult) ?? null,
      companyProfile: state.companyProfile ?? null,
    };
  } catch {
    return { result: null, companyProfile: null };
  }
}

/**
 * bootstrapStoreFromStorage — lädt den Store SYNCHRON aus dem localStorage
 * bevor der erste React-Render stattfindet.
 *
 * Wird direkt beim Modulimport aufgerufen (kein useEffect, kein Hook).
 * Dadurch ist result beim ersten Render sofort verfügbar — kein manueller Refresh nötig.
 */
function bootstrapStoreFromStorage(): void {
  try {
    // Nur bootstrap wenn der Store noch leer ist
    if (useAuditStore.getState().result !== null) return;

    const raw = localStorage.getItem('salesforge-audit-storage');
    if (!raw) return;

    const parsed = JSON.parse(raw);
    const state = parsed?.state ?? {};

    if (!state.result) return;

    // Direkt in den Store schreiben — synchron, kein Render-Cycle nötig
    useAuditStore.setState({
      result: state.result as MasterResult,
      companyProfile: state.companyProfile ?? null,
      rawAnswers: state.rawAnswers ?? {},
      currentStep: state.currentStep ?? 0,
      hasSavedSubmission: state.hasSavedSubmission ?? false,
    });
  } catch {
    // Fehler still ignorieren
  }
}

// ─── SOFORT BEIM MODULIMPORT AUSFÜHREN ───────────────────────────────────────
// Store ist zu diesem Zeitpunkt bereits erstellt (create() wurde oben aufgerufen).
// Wir füllen ihn synchron mit localStorage-Daten — bevor React irgendwas rendert.
bootstrapStoreFromStorage();

/**
 * useRehydrateOnce — Sicherheits-Fallback für den Fall, dass bootstrap
 * nicht funktioniert hat (z.B. weil localStorage beim Modulimport noch gesperrt war).
 * Zusätzlich: Subscribet auf onFinishHydration um Store-Updates sicher zu triggern.
 */
export function useRehydrateOnce() {
  useEffect(() => {
    // Fallback 1: bootstrap nochmal versuchen
    if (useAuditStore.getState().result === null) {
      bootstrapStoreFromStorage();
    }
    // Fallback 2: Zustand persist.rehydrate falls immer noch leer
    if (useAuditStore.getState().result === null) {
      useAuditStore.persist.rehydrate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/** @deprecated — nicht mehr nötig, bleibt für Kompatibilität */
export function useStoreHydrated(): boolean {
  return true;
}